import { BedDouble, Bath, Maximize, MapPin, ArrowUpRight, Gavel } from "lucide-react";
import { formatPrice } from "@/lib/api";

const statusStyles = {
  Active: "bg-navy text-white",
  Pending: "bg-brass text-white",
  Sold: "bg-navy/40 text-white",
};

export const ListingCard = ({ listing, index = 0 }) => {
  const clickable = Boolean(listing.rew_url);
  const Wrapper = clickable ? "a" : "div";
  const props = clickable
    ? { href: listing.rew_url, target: "_blank", rel: "noopener noreferrer" }
    : {};

  const priceEl = listing.price_note ? (
    <p data-testid={`listing-price-${listing.id}`} className="font-mono text-brass text-sm md:text-base uppercase tracking-[0.12em]">
      {listing.price_note}
    </p>
  ) : listing.price ? (
    <p data-testid={`listing-price-${listing.id}`} className="font-mono text-brass text-xl md:text-2xl">
      {formatPrice(listing.price)}
    </p>
  ) : (
    <p data-testid={`listing-price-${listing.id}`} className="font-mono text-ink-soft text-sm uppercase tracking-[0.12em]">
      Price on Request
    </p>
  );

  return (
    <Wrapper
      {...props}
      data-testid={`listing-card-${listing.id}`}
      className={`group relative bg-white border border-navy/10 flex flex-col ${
        clickable ? "hover:-translate-y-1.5 hover:shadow-2xl" : ""
      } transition-all duration-500 ease-out`}
    >
      {/* Index label — editorial numbering */}
      <span className="absolute top-4 right-4 z-20 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-white/80 mix-blend-difference">
        № {String(index + 1).padStart(2, "0")}
      </span>

      {/* Photo with spotlight/clipped frame */}
      <div className="relative overflow-hidden aspect-[4/3] bg-navy">
        <img
          src={listing.image_url}
          alt={listing.address}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1200ms] ease-out"
        />
        {/* Spotlight vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-navy/0 to-navy/10 pointer-events-none" />
        {/* Corner keyline (clipped frame) */}
        <span className="absolute top-3 left-3 w-6 h-6 border-l border-t border-brass" />
        <span className="absolute bottom-3 right-3 w-6 h-6 border-r border-b border-brass" />

        {/* Badges */}
        <div className="absolute bottom-4 left-4 flex flex-col gap-2">
          <span
            data-testid={`listing-status-${listing.id}`}
            className={`font-mono text-[0.62rem] uppercase tracking-[0.18em] px-3 py-1.5 ${
              statusStyles[listing.status] || "bg-navy text-white"
            }`}
          >
            {listing.status}
          </span>
          {listing.court_ordered && (
            <span
              data-testid={`listing-courtordered-${listing.id}`}
              className="font-mono text-[0.62rem] uppercase tracking-[0.18em] px-3 py-1.5 bg-white text-navy flex items-center gap-1.5"
            >
              <Gavel size={11} /> Court-Ordered
            </span>
          )}
        </div>

        {clickable && (
          <span className="absolute top-4 left-4 w-9 h-9 bg-brass text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <ArrowUpRight size={17} />
          </span>
        )}
      </div>

      <div className="p-6 md:p-7 flex flex-col flex-1">
        {priceEl}
        <h3 className="font-heading text-lg md:text-xl text-navy mt-2 leading-snug">{listing.address}</h3>
        <p className="font-body text-ink-soft text-sm flex items-center gap-1.5 mt-1.5">
          <MapPin size={14} className="text-brass" /> {listing.city}
        </p>

        {(listing.beds || listing.baths || listing.sqft) && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 pt-5 border-t border-navy/10 font-mono text-sm text-navy">
            {listing.beds != null && <span className="flex items-center gap-1.5"><BedDouble size={16} className="text-brass" /> {listing.beds}</span>}
            {listing.baths != null && <span className="flex items-center gap-1.5"><Bath size={16} className="text-brass" /> {listing.baths}</span>}
            {listing.sqft ? <span className="flex items-center gap-1.5"><Maximize size={16} className="text-brass" /> {listing.sqft.toLocaleString()} ft²</span> : null}
          </div>
        )}

        {clickable && (
          <span className="mt-6 inline-flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-navy group-hover:text-brass transition-colors">
            View on REW <ArrowUpRight size={13} />
          </span>
        )}
      </div>
    </Wrapper>
  );
};
